/**
 * Конфиг подсказок страниц для PageHelpPopover.
 * Для каждой страницы задаётся ключ перевода описания и ссылка на документацию.
 *
 * При добавлении новой страницы:
 * 1. Добавить ключ перевода описания (subtitle) в locales (ru.json, en.json).
 * 2. Добавить запись в PAGE_HELP_CONFIG с ключом страницы.
 * 3. На странице использовать <PageHelpPopover pageKey="ключ" /> рядом с заголовком.
 */

const DOCS_BASE = 'https://docs.easya.ru/'

export interface PageHelpConfigItem {
  /** Ключ перевода описания страницы (i18n, например dashboard.subtitle) */
  descriptionKey: string
  /** Ссылка на документацию для этой страницы (URL или путь относительно DOCS_BASE) */
  docsUrl: string
}

/** Ключ страницы — логический идентификатор (совпадает с путём или именем раздела) */
export type PageHelpKey = keyof typeof PAGE_HELP_CONFIG

export const PAGE_HELP_CONFIG = {
  dashboard: {
    descriptionKey: 'dashboard.subtitle',
    docsUrl: DOCS_BASE,
  },
  activity: {
    descriptionKey: 'activity.subtitle',
    docsUrl: DOCS_BASE,
  },
  lessons: {
    descriptionKey: 'lessons.subtitle',
    docsUrl: DOCS_BASE,
  },
  'lessons-student': {
    descriptionKey: 'lessons.studentSubtitle',
    docsUrl: DOCS_BASE,
  },
  'lesson-schedules': {
    descriptionKey: 'lessonSchedules.studentPageSubtitle',
    docsUrl: DOCS_BASE,
  },
  'lesson-details': {
    descriptionKey: 'lessonDetails.subtitle',
    docsUrl: DOCS_BASE,
  },
  homeworks: {
    descriptionKey: 'homeworks.subtitle',
    docsUrl: DOCS_BASE,
  },
  profile: {
    descriptionKey: 'profile.subtitle',
    docsUrl: DOCS_BASE,
  },
  'profile-security': {
    descriptionKey: 'profile.security.subtitle',
    docsUrl: DOCS_BASE,
  },
  'profile-student-form-fields': {
    descriptionKey: 'studentForms.fieldsSubtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-student-form-fields': {
    descriptionKey: 'studentForms.catalogSubtitle',
    docsUrl: DOCS_BASE,
  },
  'profile-subscription': {
    descriptionKey: 'subscription.subtitle',
    docsUrl: DOCS_BASE,
  },
  'profile-balance': {
    descriptionKey: 'balance.subtitle',
    docsUrl: DOCS_BASE,
  },
  'tts-tasks': {
    descriptionKey: 'tts.tasksSubtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-topup-requests': {
    descriptionKey: 'admin.topupRequests.subtitle',
    docsUrl: DOCS_BASE,
  },
  'profile-billing': {
    descriptionKey: 'billing.teacherPageSubtitle',
    docsUrl: DOCS_BASE,
  },
  'student-finance': {
    descriptionKey: 'billing.studentFinanceSubtitle',
    docsUrl: DOCS_BASE,
  },
  'user-finance': {
    descriptionKey: 'billing.userFinanceSubtitle',
    docsUrl: DOCS_BASE,
  },
  'profile-plans': {
    descriptionKey: 'subscription.plansSubtitle',
    docsUrl: DOCS_BASE,
  },
  groups: {
    descriptionKey: 'groups.subtitle',
    docsUrl: DOCS_BASE,
  },
  words: {
    descriptionKey: 'words.subtitle',
    docsUrl: DOCS_BASE,
  },
  users: {
    descriptionKey: 'users.subtitle',
    docsUrl: DOCS_BASE,
  },
  dictionaries: {
    descriptionKey: 'dictionaries.subtitle',
    docsUrl: DOCS_BASE,
  },
  tags: {
    descriptionKey: 'tags.subtitle',
    docsUrl: DOCS_BASE,
  },
  cards: {
    descriptionKey: 'cards.subtitle',
    docsUrl: DOCS_BASE,
  },
  teacher: {
    descriptionKey: 'teacherPage.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-users': {
    descriptionKey: 'admin.users.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-payments': {
    descriptionKey: 'admin.payments.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-finance': {
    descriptionKey: 'admin.finance.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-subscriptions': {
    descriptionKey: 'admin.subscriptions.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-plans': {
    descriptionKey: 'admin.plans.subtitle',
    docsUrl: DOCS_BASE,
  },
  news: {
    descriptionKey: 'news.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-news': {
    descriptionKey: 'admin.news.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-api-tokens': {
    descriptionKey: 'admin.apiTokens.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-chat-stickers': {
    descriptionKey: 'admin.chatStickers.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-chat-sticker-pack': {
    descriptionKey: 'admin.chatStickers.packSubtitle',
    docsUrl: DOCS_BASE,
  },
  tests: {
    descriptionKey: 'tests.subtitle',
    docsUrl: `${DOCS_BASE}student-guide-tests/`,
  },
  'user-tests': {
    descriptionKey: 'tests.userSubtitle',
    docsUrl: `${DOCS_BASE}teacher-guide-tests/`,
  },
  'tests-self-create': {
    descriptionKey: 'tests.selfCreateSubtitle',
    docsUrl: `${DOCS_BASE}student-guide-tests/#self-test-settings`,
  },
  'dictionary-fill-start': {
    descriptionKey: 'dictionaryFill.startSubtitle',
    docsUrl: DOCS_BASE,
  },
  'dictionary-fill-run': {
    descriptionKey: 'dictionaryFill.runSubtitle',
    docsUrl: DOCS_BASE,
  },
  'lesson-tests-create': {
    descriptionKey: 'tests.lessonCreateSubtitle',
    docsUrl: `${DOCS_BASE}teacher-guide-tests/#lesson-test-settings`,
  },
  notifications: {
    descriptionKey: 'notifications.subtitle',
    docsUrl: `${DOCS_BASE}notifications-browser-push/`,
  },
  'profile-notifications': {
    descriptionKey: 'notifications.preferencesHelpSubtitle',
    docsUrl: `${DOCS_BASE}notifications-browser-push/`,
  },
  accounting: {
    descriptionKey: 'accounting.subtitle',
    docsUrl: DOCS_BASE,
  },
  vacations: {
    descriptionKey: 'vacations.subtitle',
    docsUrl: DOCS_BASE,
  },
} as const

export function getPageHelpConfig(pageKey: PageHelpKey): PageHelpConfigItem {
  return PAGE_HELP_CONFIG[pageKey]
}
